import { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Landmark,
  CreditCard,
  Calendar,
  FileText,
  Activity,
  Save,
  ArrowRight,
  ArrowRightLeft,
  User,
  Link2Off,
  Layers,
} from 'lucide-react';
import type { AccountData, AccountCoverage, AccountSuccessionInfo } from '../hooks/useStatements';
import type { Statement } from '../types';
import { Button, IconButton, Input, Select, Tag, ToolbarChip, ToolbarGroup } from './ui';
import type { TagTone } from './ui';
import StatementDetailPanel from './StatementDetailPanel';
import { formatAccountName } from '../utils/accounts';
import { money } from '../lib/money';
import { CHARACTERIZATION_OPTIONS } from '../lib/enums';

export type SourceParty = 'client' | 'opposing' | 'third_party' | 'joint' | null;

interface AccountCardProps {
  account: AccountData;
  allAccounts: AccountData[];
  statements: Statement[];
  caseId: string;
  onUpdateAccount: (bankLabel: string, ownerType: string, accountType: string, propertyStatus: string) => void;
  onMergeAccounts: (sourceBankLabels: string[], targetBankLabel: string) => void;
  onMoveStatement?: (statementId: number, targetBankLabel: string) => void;
  onDeleteStatement?: (statementId: number) => void;
  onNavigateToTransactions?: (statementId: number) => void;
  onNavigateToAccountTransactions?: (bankLabel: string) => void;
  onSetAccountParty?: (bankLabel: string, party: SourceParty) => void;
  isUpdating: boolean;
  isDeletingStatement?: boolean;
  isMovingStatement?: boolean;
  clientName?: string | null;
  opposingPartyName?: string | null;
  /** This account's slice of the case coverage report (overlaps + chain). */
  coverage?: AccountCoverage | null;
  /** Succession edge naming this account as predecessor (reissue/acquisition). */
  succession?: AccountSuccessionInfo | null;
}

const SUCCESSION_VERBS: Record<string, string> = {
  reissue: 'Reissued',
  acquisition: 'Acquired',
  conversion: 'Converted',
};

function statementLabel(s: Statement): string {
  const dateStr = s.statement_end_date || s.statement_start_date;
  if (dateStr) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  if (s.source_file) return s.source_file;
  return `Statement #${s.id}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Reconciliation is assumed to succeed silently. We only surface a badge when
// there is a gap to look at: a small gap (warn) or a large gap (danger). When
// every statement balances, this returns null and nothing is shown.
function getGapSummary(reconCounts: Record<string, number>):
  | { variant: 'warning' | 'danger'; label: string }
  | null {
  const smallGaps = (reconCounts['minor_gap'] || 0) + (reconCounts['gap'] || 0) + (reconCounts['close'] || 0);
  const largeGaps = reconCounts['unreconciled'] || 0;
  if (smallGaps === 0 && largeGaps === 0) return null;
  const parts: string[] = [];
  if (largeGaps > 0) parts.push(`${largeGaps} need${largeGaps === 1 ? 's' : ''} review`);
  if (smallGaps > 0) parts.push(`${smallGaps} small gap${smallGaps === 1 ? '' : 's'}`);
  return { variant: largeGaps > 0 ? 'danger' : 'warning', label: parts.join(', ') };
}

// eslint-disable-next-line react-refresh/only-export-components -- shared helper, part of the AccountCard API (imported by AccountsTab et al.)
export function isCreditCardType(accountType: string | null | undefined): boolean {
  if (!accountType) return false;
  const s = accountType.toLowerCase();
  return s.includes('credit card') || s === 'credit_card';
}

export function AccountTypeBadge({ accountType }: { accountType: string | null | undefined }) {
  if (!accountType) return null;
  const s = (accountType || '').toLowerCase();
  let tone: TagTone = 'neutral';
  let label = accountType;
  if (s.includes('credit card') || s === 'credit_card') {
    tone = 'danger';
    label = 'Credit Card · Debt';
  } else if (s.includes('checking') || s.includes('savings') || s.includes('money market') || s === 'bank_account') {
    tone = 'good';
  } else if (s.includes('invest') || s.includes('broker') || s.includes('retire') || s.includes('401') || s.includes('ira')) {
    tone = 'info';
  } else if (s.includes('loan') || s.includes('mortgage')) {
    tone = 'danger';
  }
  return <Tag tone={tone} size="sm">{label}</Tag>;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared helper, part of the AccountCard API (imported by DocumentsTab et al.)
export function getPartyLabel(party: SourceParty, clientName?: string | null, opposingPartyName?: string | null): string {
  if (party === 'client') return clientName || 'Client';
  if (party === 'opposing') return opposingPartyName || 'Opposing Party';
  if (party === 'third_party') return 'Third party';
  if (party === 'joint') return 'Joint';
  return 'Unassigned';
}

export function PartyBadge({ party, clientName, opposingPartyName, size = 'sm' }: {
  party: SourceParty;
  clientName?: string | null;
  opposingPartyName?: string | null;
  size?: 'xs' | 'sm';
}) {
  // PROVENANCE badge: who produced these documents (source_party), which is a
  // different question from whose account this is (owner_type dropdown below).
  if (!party) return null;
  const tagSize = size === 'xs' ? 'sm' : 'md';
  if (party === 'client') {
    return (
      <Tag tone="client" size={tagSize} title={`Documents produced by ${clientName || 'the client'}`}>
        docs: {clientName || 'Client'}
      </Tag>
    );
  }
  if (party === 'opposing') {
    return (
      <Tag tone="opposing" size={tagSize} title={`Documents produced by ${opposingPartyName || 'the opposing party'} (via opposing counsel)`}>
        docs: {opposingPartyName || 'Opposing'}
      </Tag>
    );
  }
  if (party === 'third_party') {
    return (
      <Tag tone="third" size={tagSize} title="Documents produced by a third party (subpoena return, employer, institution)">
        docs: Third party
      </Tag>
    );
  }
  if (party === 'joint') {
    return (
      <Tag tone="joint" size={tagSize} title="Documents produced jointly">
        docs: Joint
      </Tag>
    );
  }
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared helper, part of the AccountCard API (imported by AssetsTab et al.)
export function getOwnerDisplayName(ownerType: string, clientName?: string | null, opposingPartyName?: string | null): string {
  // Canonical owner values are 'Client' | 'Opposing' | 'Joint' | 'Third Party' | 'Unknown'
  // (server/party.py); legacy tokens still render correctly until data is migrated.
  if (ownerType === 'Client' || ownerType === 'client' || ownerType === 'Husband') return clientName || 'Client';
  if (ownerType === 'Opposing' || ownerType === 'opposing' || ownerType === 'Wife') return opposingPartyName || 'Opposing Party';
  if (ownerType === 'Joint' || ownerType === 'joint') return 'Joint / Shared';
  if (ownerType === 'Unknown' || ownerType === 'unassigned') return 'Owner not confirmed';
  // Split-optimizer side assignments (server/ina_aggregator.owner_side): sides, not
  // ownership — Side A is labeled with the client's name, B with the opposing party's,
  // matching NetValueSplitTab's names.shortA/shortB.
  if (ownerType === 'SPOUSE_A') return clientName || 'Side A';
  if (ownerType === 'SPOUSE_B') return opposingPartyName || 'Side B';
  if (ownerType === 'UNASSIGNED') return 'Unassigned';
  // Never show a raw enum token: humanize anything unrecognized (e.g. THIRD_PARTY → Third Party).
  if (/^[A-Z0-9_]+$/.test(ownerType)) {
    return ownerType
      .toLowerCase()
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');
  }
  return ownerType;
}

function getOwnerOptions(clientName?: string | null, opposingPartyName?: string | null) {
  return [
    { value: 'Unknown', label: 'Not confirmed' },
    { value: 'Client', label: clientName ? `${clientName} (client)` : 'Client' },
    { value: 'Opposing', label: opposingPartyName ? `${opposingPartyName} (opposing party)` : 'Opposing party' },
    { value: 'Joint', label: 'Joint' },
    { value: 'Third Party', label: 'Third party / business' },
  ];
}

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'Checking', label: 'Checking' },
  { value: 'Savings', label: 'Savings' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Investment', label: 'Investment' },
  { value: 'Retirement', label: 'Retirement' },
  { value: 'Other', label: 'Other' },
];

const PROPERTY_STATUS_OPTIONS = CHARACTERIZATION_OPTIONS;

export default function AccountCard({ account, allAccounts, statements, caseId, onUpdateAccount, onMergeAccounts, onMoveStatement, onDeleteStatement, onNavigateToTransactions, onNavigateToAccountTransactions, onSetAccountParty, isUpdating, isDeletingStatement, isMovingStatement, clientName, opposingPartyName, coverage, succession }: AccountCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [editOwner, setEditOwner] = useState(account.owner_type);
  const [editAccountType, setEditAccountType] = useState(account.account_type);
  const [editPropertyStatus, setEditPropertyStatus] = useState('CP_candidate');
  const [mergeTarget, setMergeTarget] = useState('');
  const [moveStmtId, setMoveStmtId] = useState('');
  const [moveTarget, setMoveTarget] = useState('');
  const [moveNewLabel, setMoveNewLabel] = useState('');

  const gapSummary = getGapSummary(account.recon_counts || {});
  const isCreditCardAccount = isCreditCardType(account.account_type);

  const otherAccounts = allAccounts.filter(a => a.bank_label !== account.bank_label);
  const mergeOptions = [
    { value: '', label: 'Select account to merge into...' },
    ...otherAccounts.map(a => ({ value: a.bank_label, label: formatAccountName(a.bank_label) })),
  ];

  const handleSave = () => {
    onUpdateAccount(account.bank_label, editOwner, editAccountType, editPropertyStatus);
  };

  const handleMerge = () => {
    if (mergeTarget) {
      onMergeAccounts([account.bank_label], mergeTarget);
    }
  };

  const resolvedMoveTarget = moveNewLabel.trim() || moveTarget;
  const handleMove = () => {
    if (onMoveStatement && moveStmtId && resolvedMoveTarget) {
      onMoveStatement(Number(moveStmtId), resolvedMoveTarget);
      setMoveStmtId('');
      setMoveTarget('');
      setMoveNewLabel('');
    }
  };

  const dateRange = account.earliest_date || account.latest_statement_date
    ? `${formatDate(account.earliest_date)} — ${formatDate(account.latest_statement_date)}`
    : 'No dates';

  const currentParty: SourceParty = (() => {
    const parties = [...new Set(statements.map(s => s.source_party).filter(Boolean))];
    if (parties.length === 1) return parties[0] as SourceParty;
    return null;
  })();

  const detectedHolderName = statements.find(s => s.account_holder_name)?.account_holder_name ?? null;

  const coverageData = buildCoverageFromStatements(statements);
  const undatedCount = statements.filter(s =>
    !s.statement_end_date && !s.statement_start_date && !inferDateFromFilename(s.source_file || '')
  ).length;

  return (
    <div className="bg-surface-card rounded-lg border border-border overflow-hidden">
      <div className="w-full p-4 hover:bg-cream transition-colors">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <div
              className="flex items-center gap-3 min-w-0 cursor-pointer"
              onClick={() => setExpanded(!expanded)}
            >
              <div className={`p-2 rounded-md shrink-0 ${isCreditCardAccount ? 'bg-danger-soft' : 'bg-green-100'}`}>
                {isCreditCardAccount ? (
                  <CreditCard size={20} className="text-danger" />
                ) : (
                  <Landmark size={20} className="text-green-700" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="t-card text-green-900">{formatAccountName(account.bank_label)}</h3>
                  <PartyBadge party={currentParty} clientName={clientName} opposingPartyName={opposingPartyName} size="xs" />
                  {succession && (
                    <Tag
                      tone="info"
                      size="sm"
                      title={`This account number was succeeded by ${formatAccountName(succession.successor_bank_label)}${succession.succession_date ? ` on ${formatDate(succession.succession_date)}` : ''} — statements continue under the new number.`}
                    >
                      {SUCCESSION_VERBS[succession.reason] ?? 'Reissued'} → {formatAccountName(succession.successor_bank_label)}
                    </Tag>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-ink-faint">{account.account_type}</span>
                  {detectedHolderName && (
                    <>
                      <span className="text-xs text-ink-faint">·</span>
                      <span className="flex items-center gap-1 text-xs text-ink-faint">
                        <User size={12} className="text-ink-faint" />
                        {detectedHolderName}
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div
              className="flex items-center gap-6 mt-3 text-xs text-ink-faint cursor-pointer"
              onClick={() => setExpanded(!expanded)}
            >
              <span className="flex items-center gap-1"><Calendar size={12} /> {dateRange}</span>
              <span className="flex items-center gap-1"><FileText size={12} /> {account.statement_count} statement{account.statement_count !== 1 ? 's' : ''}</span>
              {gapSummary && (
                <span className={`flex items-center gap-1 font-medium ${gapSummary.variant === 'danger' ? 'text-danger' : 'text-warn'}`}>
                  <Activity size={12} /> {gapSummary.label}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 shrink-0 ml-4">
            {gapSummary && (
              <Tag tone={gapSummary.variant === 'danger' ? 'danger' : 'warn'}>{gapSummary.label}</Tag>
            )}
            <div className="text-center min-w-30">
              <p className="text-green-900">
                <span className="t-num-hero whitespace-nowrap">{money(account.latest_balance)}</span>
              </p>
              <p className="t-caption">Latest Balance</p>
            </div>
            <IconButton
              icon={expanded ? ChevronUp : ChevronDown}
              size="sm"
              variant="ghost"
              onClick={() => setExpanded(!expanded)}
              aria-label={expanded ? 'Collapse' : 'Expand'}
            />
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border-subtle p-4 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="Owner"
              options={getOwnerOptions(clientName, opposingPartyName)}
              value={editOwner}
              onChange={(e) => setEditOwner(e.target.value)}
            />
            <Select
              label="Account Type"
              options={ACCOUNT_TYPE_OPTIONS}
              value={editAccountType}
              onChange={(e) => setEditAccountType(e.target.value)}
            />
            <Select
              label="Property Status"
              options={PROPERTY_STATUS_OPTIONS}
              value={editPropertyStatus}
              onChange={(e) => setEditPropertyStatus(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button size="md" icon={Save} onClick={handleSave} loading={isUpdating}>
              Save Changes
            </Button>
            {onSetAccountParty && (
              <div className="flex items-center gap-2 ml-4 border-l border-border-subtle pl-4" title="Who handed these documents to the firm — different from whose account it is">
                <span className="text-xs text-ink-faint font-medium whitespace-nowrap">Docs produced by:</span>
                <ToolbarGroup>
                  {(['client', 'opposing', 'third_party', null] as SourceParty[]).map((p) => {
                    const label = p === 'client' ? (clientName || 'Client') : p === 'opposing' ? (opposingPartyName || 'Opposing') : p === 'third_party' ? 'Third party' : 'Unset';
                    return (
                      <ToolbarChip
                        key={p ?? 'unset'}
                        selected={currentParty === p}
                        onClick={() => onSetAccountParty(account.bank_label, p)}
                      >
                        {label}
                      </ToolbarChip>
                    );
                  })}
                </ToolbarGroup>
              </div>
            )}
            {onNavigateToAccountTransactions && (
              <Button
                size="md"
                variant="ghost"
                iconTrailing={ArrowRight}
                onClick={() => onNavigateToAccountTransactions(account.bank_label)}
                className="ml-auto"
                title="View all transactions for this account"
              >
                View All Transactions
              </Button>
            )}
          </div>

          {coverageData.length > 0 && (
            <div>
              <h4 className="t-label text-green-900 mb-2">Statement Coverage</h4>
              <div className="space-y-2">
                {coverageData.map(yearGroup => (
                  <div key={yearGroup.year} className="flex items-center gap-2">
                    <span className="text-xs font-medium text-ink-faint w-10 text-right flex-shrink-0">
                      {yearGroup.year}
                    </span>
                    <div className="grid grid-cols-12 gap-1 flex-1">
                      {yearGroup.months.map(m => {
                        // Months that have a statement and balance show green
                        // ("on file"); small gaps (clay) and large gaps (red)
                        // get their own color so the eye still jumps to
                        // problems. Cast to string so any legacy gap status
                        // (gap/close) is still caught as a gap, never as on-file.
                        const cov = m.status as string | null;
                        let bgClass = 'bg-cream-dark border-cream-dark text-ink-faint';
                        let tooltip = `${m.label}: No statement`;
                        if (cov === 'minor_gap' || cov === 'gap' || cov === 'close') {
                          bgClass = 'bg-clay-100 border-clay-300 text-clay-800';
                          tooltip = `${m.label}: Small gap ${m.gap !== null ? `(${money(m.gap)})` : ''} (${m.txnCount} txns)`;
                        } else if (cov === 'unreconciled') {
                          bgClass = 'bg-danger-soft border-danger text-danger';
                          tooltip = `${m.label}: Needs review ${m.gap !== null ? `(${money(m.gap)} gap)` : ''} (${m.txnCount} txns)`;
                        } else if (cov) {
                          // Reconciled / manually reconciled / not-applicable /
                          // unknown — a statement is on file and there is no gap
                          // to investigate, so show it green.
                          bgClass = 'bg-green-100 border-green-300 text-green-800';
                          tooltip = `${m.label}: Statement on file (${m.txnCount} txns)`;
                        }
                        const overlapping = m.stmtCount >= 2;
                        if (overlapping) {
                          tooltip += ` — ${m.stmtCount} statements cover this month (possible double-count)`;
                        }
                        return (
                          <div
                            key={m.monthIndex}
                            className={`h-7 rounded border text-2xs font-medium flex items-center justify-center relative ${bgClass} ${overlapping ? 'ring-2 ring-clay-500' : ''}`}
                            title={tooltip}
                          >
                            {m.abbr}
                            {overlapping && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-clay-600 text-white text-2xs font-semibold leading-none flex items-center justify-center">
                                {m.stmtCount}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-4 mt-2 text-2xs text-ink-faint">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-green-100 border border-green-300" /> On File</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-clay-100 border border-clay-300" /> Small Gap</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-danger-soft border border-danger" /> Needs Review</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-cream-dark border border-cream-dark" /> Missing</span>
              </div>
              {undatedCount > 0 && (
                <div className="mt-2 text-xs text-clay-700 bg-clay-50 border border-clay-200 rounded px-2 py-1">
                  {undatedCount} statement{undatedCount !== 1 ? 's' : ''} not shown — no date could be determined. See the statement list below for details.
                </div>
              )}
              {coverage && (coverage.overlaps.length > 0 || coverage.chain.some(c => c.severity !== 'low')) && (
                <div className="mt-3 space-y-2">
                  <h5 className="text-xs font-medium text-green-900">Coverage integrity</h5>
                  {coverage.overlaps.map((o, i) => (
                    <div
                      key={`ov-${i}`}
                      className={`flex items-start gap-2 text-xs rounded px-2 py-2 border ${
                        o.classification === 'conflicting'
                          ? 'bg-danger-soft border-danger text-danger'
                          : 'bg-clay-50 border-clay-200 text-clay-800'
                      }`}
                    >
                      <Layers size={12} className="mt-(--space-hair) flex-shrink-0" />
                      <span>
                        {o.classification === 'conflicting'
                          ? `Two productions of this account disagree for ${o.overlap_start} – ${o.overlap_end} — review both statements; this is a production integrity issue, not a data bug.`
                          : `Statements overlap ${o.overlap_start} – ${o.overlap_end} (${o.overlap_days} days${
                              o.double_counted_transactions
                                ? `, ${o.double_counted_transactions} duplicated transactions`
                                : ''
                            }) — totals double-count until one copy is removed.`}
                      </span>
                    </div>
                  ))}
                  {coverage.chain.filter(c => c.severity !== 'low').map((c, i) => (
                    <div
                      key={`ch-${i}`}
                      className={`flex items-start gap-2 text-xs rounded px-2 py-2 border ${
                        c.kind === 'continuity_break'
                          ? 'bg-danger-soft border-danger text-danger'
                          : 'bg-clay-50 border-clay-200 text-clay-800'
                      }`}
                    >
                      <Link2Off size={12} className="mt-(--space-hair) flex-shrink-0" />
                      <span>{c.detail}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {coverageData.length === 0 && undatedCount > 0 && (
            <div>
              <h4 className="t-label text-green-900 mb-2">Statement Coverage</h4>
              <div className="text-xs text-clay-700 bg-clay-50 border border-clay-200 rounded px-2 py-1">
                {undatedCount} statement{undatedCount !== 1 ? 's have' : ' has'} no date information. Dates could not be extracted from the PDF or inferred from the filename. See the statement list below.
              </div>
            </div>
          )}

          {statements.length > 0 && (
            <div className="border-t border-border-subtle pt-4">
              <StatementDetailPanel
                statements={statements}
                accountType={account.account_type}
                caseId={caseId}
                onDeleteStatement={onDeleteStatement}
                isDeleting={isDeletingStatement}
                onNavigateToTransactions={
                  onNavigateToAccountTransactions
                    ? () => onNavigateToAccountTransactions(account.bank_label)
                    : onNavigateToTransactions
                }
              />
            </div>
          )}

          {otherAccounts.length > 0 && (
            <div className="border-t border-border-subtle pt-4">
              <h4 className="t-label text-green-900 mb-2">Merge Account</h4>
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <Select
                    options={mergeOptions}
                    value={mergeTarget}
                    onChange={(e) => setMergeTarget(e.target.value)}
                  />
                </div>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={handleMerge}
                  disabled={!mergeTarget}
                >
                  Merge
                </Button>
              </div>
              <p className="text-xs text-ink-faint mt-1">
                This will move all statements from this account into the selected target account.
              </p>
            </div>
          )}

          {onMoveStatement && statements.length > 0 && (
            <div className="border-t border-border-subtle pt-4">
              <h4 className="t-label text-green-900 mb-2 flex items-center gap-2">
                <ArrowRightLeft size={16} className="text-ink-faint" />
                Move a Statement
              </h4>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex-1 min-w-40">
                  <Select
                    options={[
                      { value: '', label: 'Select statement to move...' },
                      ...statements.map(s => ({ value: String(s.id), label: statementLabel(s) })),
                    ]}
                    value={moveStmtId}
                    onChange={(e) => setMoveStmtId(e.target.value)}
                  />
                </div>
                <div className="flex-1 min-w-40">
                  <Select
                    options={[
                      { value: '', label: 'Move to existing account...' },
                      ...otherAccounts.map(a => ({ value: a.bank_label, label: formatAccountName(a.bank_label) })),
                    ]}
                    value={moveTarget}
                    onChange={(e) => { setMoveTarget(e.target.value); if (e.target.value) setMoveNewLabel(''); }}
                    disabled={!!moveNewLabel.trim()}
                  />
                </div>
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={handleMove}
                  disabled={!moveStmtId || !resolvedMoveTarget}
                  loading={isMovingStatement}
                >
                  Move
                </Button>
              </div>
              <div className="mt-2">
                <Input
                  type="text"
                  value={moveNewLabel}
                  onChange={(e) => { setMoveNewLabel(e.target.value); if (e.target.value.trim()) setMoveTarget(''); }}
                  placeholder="…or type a new account name to split it out"
                />
              </div>
              <p className="text-xs text-ink-faint mt-1">
                Use this to undo a merge or fix a misfiled statement — it moves the statement and its transactions to another account, or splits it into a brand-new one.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface CoverageMonth {
  monthIndex: number;
  abbr: string;
  label: string;
  status: 'reconciled' | 'minor_gap' | 'unreconciled' | 'unknown' | null;
  gap: number | null;
  txnCount: number;
  stmtCount: number;
}

interface CoverageYear {
  year: number;
  months: CoverageMonth[];
}

const MONTH_NAMES_MAP: Record<string, number> = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
  apr: 3, april: 3, may: 4, jun: 5, june: 5, jul: 6, july: 6,
  aug: 7, august: 7, sep: 8, sept: 8, september: 8,
  oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11,
};

function inferDateFromFilename(filename: string): { year: number; month: number } | null {
  const name = filename.replace(/\.[^.]+$/, '');

  for (const pattern of [
    /(?<month>[A-Za-z]+)\s*[-_]?\s*(?<year>20\d{2})/,
    /(?<year>20\d{2})\s*[-_]?\s*(?<month>[A-Za-z]+)/,
  ]) {
    const m = name.match(pattern);
    if (m?.groups) {
      const monthNum = MONTH_NAMES_MAP[m.groups.month.toLowerCase()];
      const year = parseInt(m.groups.year);
      if (monthNum !== undefined && !isNaN(year)) {
        return { year, month: monthNum };
      }
    }
  }

  let m = name.match(/(?<year>20\d{2})\s*[-_/]\s*(?<month>\d{1,2})/);
  if (m?.groups) {
    const month = parseInt(m.groups.month) - 1;
    const year = parseInt(m.groups.year);
    if (month >= 0 && month <= 11) return { year, month };
  }

  m = name.match(/(?<month>\d{1,2})\s*[-_/]\s*(?<year>20\d{2})/);
  if (m?.groups) {
    const month = parseInt(m.groups.month) - 1;
    const year = parseInt(m.groups.year);
    if (month >= 0 && month <= 11) return { year, month };
  }

  return null;
}

function buildCoverageFromStatements(stmts: Statement[]): CoverageYear[] {
  if (!stmts || stmts.length === 0) return [];

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const monthMap = new Map<string, { status: string | null; gap: number | null; txnCount: number; stmtCount: number }>();

  for (const s of stmts) {
    let year: number;
    let month: number;

    const dateStr = s.statement_end_date || s.statement_start_date;
    if (dateStr) {
      const d = new Date(dateStr);
      year = d.getFullYear();
      month = d.getMonth();
    } else {
      const inferred = inferDateFromFilename(s.source_file || '');
      if (!inferred) continue;
      year = inferred.year;
      month = inferred.month;
    }

    const key = `${year}-${month}`;
    const existing = monthMap.get(key);
    // Keep the highest-txn-count statement for the cell's status, but COUNT
    // every statement that lands in the month — 2+ means overlapping
    // productions, which must be visible, never silently collapsed.
    if (!existing || (s.transaction_count || 0) > existing.txnCount) {
      monthMap.set(key, {
        status: dateStr ? s.reconciliation_status : 'unknown',
        gap: s.reconciliation_gap,
        txnCount: s.transaction_count || 0,
        stmtCount: (existing?.stmtCount || 0) + 1,
      });
    } else {
      existing.stmtCount += 1;
    }
  }

  if (monthMap.size === 0) return [];

  const allKeys = Array.from(monthMap.keys());
  const years = allKeys.map(k => parseInt(k.split('-')[0]));
  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);

  const result: CoverageYear[] = [];

  for (let year = minYear; year <= maxYear; year++) {
    const months: CoverageMonth[] = [];
    for (let mi = 0; mi < 12; mi++) {
      const key = `${year}-${mi}`;
      const data = monthMap.get(key);
      months.push({
        monthIndex: mi,
        abbr: monthNames[mi],
        label: `${monthNames[mi]} ${year}`,
        status: data ? (data.status as CoverageMonth['status']) : null,
        gap: data ? data.gap : null,
        txnCount: data ? data.txnCount : 0,
        stmtCount: data ? data.stmtCount : 0,
      });
    }
    result.push({ year, months });
  }

  return result;
}
